<template>
  <div>
    <THeading
      tag="h2"
      :level="3"
      class="mb-6"
    >
      {{ t('popularRVRentals') }}
    </THeading>

    <Grid
      :max-results="pageSize"
      :rows="2"
      :sm-cols="2"
      :md-cols="3"
      :lg-cols="4"
      :xl-cols="5"
    >
      <template #default="{ getItemClass }">
        <CardRv
          v-for="(rv, index) in featuredRvs"
          :key="rv.Id"
          :index="index"
          :search-request-id="searchRequestId || ''"
          :rv="rv"
          cta="featured"
          discovery-source="Home"
          :class="getItemClass(index)"
          @visible:rv="queueViewableImpression"
        />
      </template>
    </Grid>

    <div class="flex justify-end mt-12">
      <TButton
        :to="localePath({ name: 'rv-search' })"
        outline
      >
        {{ t('actions.showMore') }}
      </TButton>
    </div>
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  noGeolocationPartition?: boolean
}>()

const NorthAmericaBoundingBox = {
  swLng: '-179.9',
  swLat: '18.8163608007951',
  neLng: '-52.5198306260026',
  neLat: '83.1473634999412',
}

const localePath = useLocalePath()
const { t } = useI18n()

const runtimeConfig = useRuntimeConfig()
const { geolocation } = useGeolocation()

const pageSize = 10
const params = computed(() => {
  const params = {
    PageSize: 0,
    FeaturedPageSize: pageSize,
  }

  if (!props.noGeolocationPartition && geolocation.value) {
    const { latitude, longitude, countryCode } = geolocation.value
    const { supportedCountries } = runtimeConfig.public
    if (latitude && longitude && countryCode && supportedCountries.includes(countryCode.toLowerCase())) {
      return {
        ...params,
        SearchLat: latitude,
        SearchLng: longitude,
        country: countryCode,
      }
    }
  }

  return {
    ...params,
    ...NorthAmericaBoundingBox,
  }
})

const { $rentals } = useNuxtApp()
const { data } = await useAsyncData(
  'featuredRVs',
  () => $rentals('/api/rvlistings/unified-search', { params: params.value }),
  {
    transform: transformFeaturedRVs,
    watch: [params],
  },
)

const searchRequestId = computed(() => data.value?.Id || '')
const featuredRvs = computed(() => data.value?.ListRVs || [])

const { queueViewableImpression } = useRvViewableImpressions({
  pageNumber: 1,
  pageSize: pageSize,
})
</script>

<i18n lang="json">
{
  "en": {
    "popularRVRentals": "Popular RV Rentals"
  },
  "fr": {
    "popularRVRentals": "VR Populaires"
  }
}
</i18n>
